/**
 * 健康点検の質問の表示名。
 *
 * アプリは健康点検の質問を、画面の言語にかかわらず英語の文言のまま送る
 * （apps/mobile の health_check_screen.dart が getLocale('en') の文言を name にする）。
 * サーバーはその name をそのまま保存するので、管理画面で質問を画面の言語に置き換える。
 * 車両点検（VehicleInspectionDetails.tsx の `parts.<name>`）と同じく、保存の形は変えず表示で置き換える。
 *
 * 文言をそのまま翻訳キーにすると、キーの区切り（`.`・`:`）を含む文言で引けなくなるので、
 * 送られる文言から短いキーへ対応させ、locale の `view.health-check.questions.<キー>` を引く。
 * 日本語の文言で保存された記録（以前のアプリ）も同じキーへ対応させる。
 * 対応表に無い name（将来の質問・手入力の試験データ）は、保存されたまま出す。
 */
import type { TFunction } from 'i18next';

export const HEALTH_QUESTION_KEYS: Readonly<Record<string, string>> = {
  // アプリの app_en.arb の health_check_question_*（今のアプリが送る文言）。
  'What is your body temperature?': 'temperature',
  'Are you in good health (dizziness, palpitations, shortness of breath, etc)?': 'good_health',
  'Is there any alcohol left?': 'alcohol',
  'Do you have a fever?': 'fever',
  'Are you tired?': 'tired',
  'Did you get enough sleep?': 'sleep',
  'Do you have any vision or visual acuity problems?': 'vision',
  "Don't you feel sick?": 'sick',
  'Are you taking any medications that affect your driving?': 'medication',
  // アプリの app_ja.arb の health_check_question_*（日本語の文言で保存された記録）。
  'あなたの体温は何度ですか?': 'temperature',
  '健康状態は良いか（めまい 、動悸、息切れ等）?': 'good_health',
  'アルコールは残っていないか?': 'alcohol',
  '熱はないか?': 'fever',
  '疲れはないか?': 'tired',
  '睡眠は十分とれたか?': 'sleep',
  '視覚、視力に問題はないか?': 'vision',
  '気分は悪くないか?': 'sick',
  '運転に影響のある服薬はないか?': 'medication',
};

/** 保存された質問の name を、画面の言語の表示名にする。 */
export function healthQuestionLabel(name: unknown, t: TFunction): string {
  const text = typeof name === 'string' ? name : '';
  const key = HEALTH_QUESTION_KEYS[text.trim()];
  if (!key) return text;
  return t(`view.health-check.questions.${key}`, { ns: 'driver', defaultValue: text });
}

/**
 * A36（2026-11-03 和田様の UAT）。健康点検の表（縦＝日付・横＝設問）の列の並び。体温は別の列で先頭。
 * アプリの設問の順（health_check_screen.dart）と同じ。
 */
export const HEALTH_QUESTION_ORDER: readonly string[] = [
  'good_health',
  'alcohol',
  'fever',
  'tired',
  'sleep',
  'vision',
  'sick',
  'medication',
];

/** CSV の設問の列の見出し（今の CSV と同じく英語）。値は OK（問題なし）か NG。 */
export const HEALTH_QUESTION_CSV_HEADERS: Readonly<Record<string, string>> = {
  good_health: 'Good Health',
  alcohol: 'No Alcohol Left',
  fever: 'No Fever',
  tired: 'Not Tired',
  sleep: 'Enough Sleep',
  vision: 'No Vision Problems',
  sick: 'Not Feeling Sick',
  medication: 'No Affecting Medication',
};

export interface HealthAnswers {
  /** 体温（入力のまま。例 "36.5"）。無ければ空。 */
  temperature: string;
  /** 体温が基準未満なら true（アプリが status で送る）。以前の記録・無いときは null。 */
  temperatureOk: boolean | null;
  /** 設問のキー（HEALTH_QUESTION_ORDER）ごとの答え。true が問題なし。無い設問は null。 */
  answers: Record<string, boolean | null>;
  /** 対応表に無い設問（保存された name のまま）。 */
  others: { name: string; status: boolean | null }[];
}

/**
 * 健康点検の記録の設問（details.driver_healthcheck[0].item）を、表と CSV の列の値にする。
 *
 * 今のアプリは体温も { name, status, note }（値は note）で送る。以前のアプリの記録は体温を
 * { 設問: "36.5" }（name・status なし）で保存していた（healthCheckSummary と同じ扱い）。
 */
export function healthAnswers(items: unknown): HealthAnswers {
  const result: HealthAnswers = { temperature: '', temperatureOk: null, answers: {}, others: [] };
  HEALTH_QUESTION_ORDER.forEach((key) => {
    result.answers[key] = null;
  });
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item || typeof item !== 'object') return;
    const record = item as Record<string, unknown>;
    const status = typeof record.status === 'boolean' ? record.status : null;
    if (typeof record.name !== 'string') {
      // 以前のアプリの体温（{ 設問: "36.5" }）。
      Object.entries(record).forEach(([question, value]) => {
        if (HEALTH_QUESTION_KEYS[question.trim()] === 'temperature' && value !== null && value !== undefined) {
          result.temperature = String(value);
        }
      });
      return;
    }
    const key = HEALTH_QUESTION_KEYS[record.name.trim()];
    if (key === 'temperature') {
      result.temperature = typeof record.note === 'string' ? record.note : '';
      result.temperatureOk = status;
    } else if (key) {
      result.answers[key] = status;
    } else {
      result.others.push({ name: record.name, status });
    }
  });
  return result;
}
